import { useMemo } from 'react';
import {
  EntityWithDocumentFullResponse,
  ShareholderDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import {
  ListShareholder,
  RenderIndividualPersonsDetails,
  renderEntityContactDetails,
  renderEntityDetailsSection,
  renderEntityDocuments,
  renderEntityTaxResidence,
} from '@/modules/entity/utils/entity-info/entity-info.tsx';

export const RenderAssociationEntityDetails = ({
  association_entity_details_id,
  document_files,
  ...entity_details
}: Partial<EntityWithDocumentFullResponse>) => {
  const {
    country,
    abn,
    registered_office,
    association_number,
    website,
    company_name,
    shareholders,
    officers,
    account_holders_gin,
    sponsoring_entity_name,
    is_us_person,
    non_us_person_certification,
    tax_residence,
    is_managed_by_another_institution,
    crs_status,
  } = association_entity_details_id || {};

  const {
    bank_name,
    bank_account_bsb,
    bank_account_swift,
    bank_account_iban,
    bank_account_routing_number,
    bank_account_number,
    bank_account_name,
  } = entity_details || {};

  const subShareholders = useMemo<ListShareholder[]>(() => {
    const officers2 = (association_entity_details_id?.officers || [])?.map(
      (subShareholder: ShareholderDetails, indexSubShareholder): ListShareholder => ({
        shareholder: subShareholder,
        indexShareholder: 0,
        indexSubShareholder,
        person_details_key: 'officers',
        navName: `Public Officer`,
      }),
    );
    const shareholders2 = (association_entity_details_id?.shareholders || [])?.map(
      (subShareholder: ShareholderDetails, indexSubShareholder): ListShareholder => ({
        shareholder: subShareholder,
        indexShareholder: 0,
        indexSubShareholder,
        person_details_key: 'shareholders',
        navName: `Shareholder`,
      }),
    );

    return [...officers2, ...shareholders2];
  }, [association_entity_details_id?.shareholders, association_entity_details_id?.officers]);

  return (
    <>
      {renderEntityContactDetails(entity_details)}

      {renderEntityDetailsSection({
        name: 'Association details',
        items: [
          { label: 'Name', value: company_name },
          { label: 'Country', value: country },
          { label: 'ABN', value: abn },
          { label: 'Registered office', value: registered_office },
          { label: 'Association number', value: association_number },
          { label: 'Website', value: website },
        ],
      })}
      {RenderIndividualPersonsDetails({
        persons: officers,
        role: 'Officer',
        listStakeholders: subShareholders,
      })}
      {RenderIndividualPersonsDetails({
        persons: shareholders,
        role: 'Shareholder',
        listStakeholders: subShareholders,
      })}
      {renderEntityTaxResidence(tax_residence)}
      {renderEntityDetailsSection({
        items: [
          { label: 'GIIN', value: account_holders_gin },
          { label: 'Sponsoring entity name', value: sponsoring_entity_name },
          {
            label:
              'Is the Account Holder a specified U.S. person? A U.S. person includes a U.S. citizen or resident alien of the U.S. even if residing outside the U.S.',
            value: is_us_person,
          },
          { label: 'Non U.S Person Certification', value: non_us_person_certification },

          {
            label: 'Is the entity an Investment Entity managed by another Financial Institution?',
            value: is_managed_by_another_institution,
          },
          { label: 'CRS status', value: crs_status },
        ],
      })}
      {renderEntityDetailsSection({
        name: 'Payment instructions',
        items: [
          { label: 'Bank name', value: bank_name },
          { label: 'BSB', value: bank_account_bsb },
          { label: 'SWIFT/BIC', value: bank_account_swift },
          { label: 'IBAN', value: bank_account_iban },
          { label: 'Routing number', value: bank_account_routing_number },
          { label: 'Account number', value: bank_account_number },
          { label: 'Account name', value: bank_account_name },
        ],
      })}
      {renderEntityDocuments(document_files || [])}
    </>
  );
};
